/**
 * ฟอร์มอุปกรณ์ — ยกมาจากโมดัลใน DevicePage (หน้าเต็ม 28 ต.ค. 2026 · ต้นแบบ components/company/companyForm.ts)
 *
 * ใช้ร่วมกันระหว่าง DeviceViewPage (อ่านอย่างเดียว) และ DeviceFormPage (เพิ่ม/แก้ไข) — mapper ตัวเดียว
 * (deviceToForm) กันสองหน้าแสดงค่าไม่ตรงกัน · กติกาเดิมของโมดัลอยู่ครบ (ดูคอมเมนต์แต่ละฟังก์ชัน)
 */
import { Device, DeviceCondition, DeviceStatus } from '../../types/device';
import { statusColors } from '../../config/designSystem';
import { getLocalDateString } from '../../utils/dateUtils';

export type DeviceFormMode = 'create' | 'edit';

export interface DeviceForm {
  code: string;
  // Widened to string|number — select dropdowns produce strings; coerced before submit
  device_type_id: number | string | null;
  brand_id: number | string | null;
  device_model_id: number | string | null;
  model: string;
  serial_number: string;
  imei: string;
  status: DeviceStatus;
  purchase_date: string;
  notes: string;
  location?: string;
  // New fields for data migration
  location_note?: string;
  device_number?: number | null;
  // Inventory management fields
  is_serialized: boolean;
  quantity: number;
  unit: string;
  minimum_quantity: number;
  // Procurement/Asset fields - ข้อมูลการจัดซื้อ/ครุภัณฑ์
  supplier_id: number | string | null;
  purchase_price: number | null;
  invoice_number: string;
  receive_date: string;
  condition: DeviceCondition | null;
  book_value: number | null;
  asset_number: string;
}

/** ค่าตั้งต้นของฟอร์มเพิ่มใหม่ — วันที่ซื้อ default เป็นวันนี้ (เหมือนโมดัลเดิม) */
export const emptyDeviceForm = (code = ''): DeviceForm => ({
  code,
  device_type_id: null,
  brand_id: null,
  device_model_id: null,
  model: '',
  serial_number: '',
  imei: '',
  status: DeviceStatus.AVAILABLE,
  purchase_date: getLocalDateString(),
  notes: '',
  location: '',
  location_note: '',
  device_number: null,
  is_serialized: true,
  quantity: 1,
  unit: 'เครื่อง',
  minimum_quantity: 0,
  supplier_id: null,
  purchase_price: null,
  invoice_number: '',
  receive_date: '',
  condition: DeviceCondition.NEW,
  book_value: null,
  asset_number: '',
});

/** เครื่องจาก API → ค่าฟอร์ม (หน้าดูและหน้าแก้ไขใช้ตัวเดียวกัน) */
export const deviceToForm = (device: Device): DeviceForm => ({
  // BUG-S347-NULLCODE-DEVICES: code/model อาจเป็น null (device สร้างผ่าน API โดยไม่ระบุรหัส —
  // code optional ที่ backend) → guard `|| ''` เหมือน field อื่น กัน validateForm (`formData.code.trim()`)
  // crash แล้ว save ล้มเงียบ (=BUG-S342-NULLCODE ที่ suppliers)
  code: device.code || '',
  device_type_id: device.device_type_id || null,
  brand_id: device.brand_id || null,
  device_model_id: device.device_model_id || null,
  model: device.model || '',
  serial_number: device.serial_number || '',
  imei: device.imei || '',
  status: device.status,
  purchase_date: device.purchase_date ? device.purchase_date.split('T')[0] : '',
  notes: device.notes || '',
  location: device.location || '',
  location_note: device.location_note || '',
  device_number: device.device_number || null,
  is_serialized: device.is_serialized ?? true,
  // KD3 S2130 (BUG-S2130-002): `|| 1` เปลี่ยนยอด 0 (ของออกไปอยู่ในสัญญาหมด) เป็น 1 ตอนเปิดฟอร์ม
  // → กดบันทึก (แม้แก้แค่หมายเหตุ) = ledger ADJUSTMENT 0→1 ของงอกขึ้นมาในคลัง 1 ชิ้น
  quantity: device.quantity ?? 1,
  unit: device.unit || 'เครื่อง',
  minimum_quantity: device.minimum_quantity || 0,
  supplier_id: device.supplier_id ?? null,
  // `??` ไม่ใช่ `||` — เครื่องที่ราคาซื้อ 0 บาทต้องโหลดมาเป็น 0 ไม่ใช่ว่าง
  // ไม่งั้นเปิดฟอร์มมาเห็นช่องว่าง แล้วโดนบังคับกรอกทั้งที่ระบบมีค่าอยู่แล้ว
  purchase_price: device.purchase_price ?? null,
  invoice_number: device.invoice_number || '',
  receive_date: device.receive_date ? device.receive_date.split('T')[0] : '',
  condition: device.condition || DeviceCondition.NEW,
  book_value: device.book_value || null,
  asset_number: device.asset_number || '',
});

/** ถือว่า "มีค่าอยู่แล้ว" หรือไม่ — `0` นับว่ามีค่า (ราคาซื้อ 0 บาทเป็นค่าที่ถูกต้อง เช่น ของแถม)
 *  ถ้าใช้ falsy check ธรรมดา เครื่องราคา 0 จะถูกมองว่า "ว่าง" แล้วหลุดการบังคับไป */
const hasProcurementValue = (v: number | null | undefined): boolean => v !== null && v !== undefined;

/**
 * "ผู้จำหน่าย" / "ราคาซื้อ" เป็นฟิลด์บังคับหรือไม่ — กติกาเดียวกับค่ายมือถือของซิม
 *
 * บังคับเมื่อ: สร้างเครื่องใหม่ · หรือแก้เครื่องที่ "มีค่าอยู่แล้ว" (กันคนล้างค่าทิ้งโดยไม่ตั้งใจ)
 * ไม่บังคับเมื่อ: เปิดแก้เครื่องเก่าที่ค่านั้นว่างอยู่แต่เดิม
 *
 * ทำไม: เดิมบังคับทุกครั้งที่บันทึก ⇒ บน prod มีเครื่องที่ยังใช้งาน 799 เครื่อง แต่ 683 เครื่อง
 * (85%) ไม่มีผู้จำหน่าย/ราคาซื้อ → **แก้อะไรจากหน้าจอไม่ได้เลย** รวมถึงช่อง IMEI ที่เพิ่งเพิ่ม
 * (ผู้ใช้เห็นช่อง แต่กดบันทึกแล้วโดนบล็อกด้วย error คนละเรื่อง)
 *
 * และ **ห้ามบังคับให้กรอก** เพราะคนที่มาแก้ IMEI ไม่มีทางรู้ว่าเครื่องนี้ซื้อจากใครราคาเท่าไร —
 * บังคับตอนนั้นได้แค่การเดา · ราคาซื้อที่เดาลงไปจะกลายเป็น "ตัวเลขที่ดูเหมือนข้อมูลจริง
 * แยกไม่ออกจากของแท้" แล้วไหลเข้ารายงานมูลค่าทรัพย์สิน = เพี้ยนแบบกู้คืนไม่ได้
 * อันตรายกว่าเคสค่ายซิมมาก (ค่ายเดาผิดยังเห็นได้ว่าผิด แต่ราคาเดาผิดดูเนียน)
 */
export const isDeviceProcurementRequired = (
  mode: 'create' | 'edit' | 'view',
  originalValue: number | null | undefined
): boolean => mode === 'create' || hasProcurementValue(originalValue);

/** แยกหลาย serial จาก textarea: คั่นด้วย , หรือขึ้นบรรทัดใหม่ → trim + ตัดว่าง + dedup (คงลำดับ) */
export const parseSerialNumbers = (raw: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of (raw || '').split(/[\n,]+/)) {
    const s = part.trim();
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
};

/** ตรวจฟอร์มทั้งหมดในครั้งเดียว (inline error) · original = เครื่องตอนเปิดฟอร์ม (โหมดแก้ไข) */
export const validateDeviceForm = (
  formData: DeviceForm,
  mode: DeviceFormMode,
  original?: Pick<Device, 'supplier_id' | 'purchase_price'> | null
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!formData.code.trim()) {
    errors.code = 'กรุณาใส่รหัสอุปกรณ์';
  }
  if (!formData.device_type_id) {
    errors.device_type_id = 'กรุณาเลือกประเภทอุปกรณ์';
  }
  // ยี่ห้อเป็น optional ไม่ต้องบังคับ
  // ตรวจสอบ serial number เฉพาะอุปกรณ์ที่ต้องติดตาม serial
  if (formData.is_serialized && !formData.serial_number.trim()) {
    errors.serial_number = 'กรุณาใส่หมายเลขซีเรียล';
  }
  // ตรวจสอบจำนวนสำหรับอุปกรณ์ที่ไม่ติดตาม serial
  // KD3 S2130 (BUG-S2130-001): ตอนแก้ไข ยอด 0 คือความจริงปกติ (ของออกไปอยู่ในสัญญาหมด) — backend รับ ge=0
  // เดิมบังคับ > 0 ทุกโหมด → แถวที่ยอดเหลือ 0 แก้ช่องไหนก็บันทึกไม่ได้ · สร้างใหม่ยังต้อง ≥ 1
  const minQuantity = mode === 'create' ? 1 : 0;
  if (!formData.is_serialized && !(Number(formData.quantity) >= minQuantity)) {
    errors.quantity = minQuantity === 1 ? 'กรุณาใส่จำนวนที่ถูกต้อง' : 'จำนวนต้องเป็น 0 หรือมากกว่า';
  }
  // ตรวจสอบข้อมูลการจัดซื้อ — บังคับเฉพาะตอนสร้างใหม่ หรือแถวที่มีค่าอยู่แล้ว
  // (ดู isDeviceProcurementRequired) · เดิมบังคับทุกครั้งที่บันทึก ⇒ เครื่องเก่าที่ไม่มี
  // ผู้จำหน่าย/ราคาซื้อ (บน prod 683 จาก 799 = 85%) แก้อะไรจากหน้าจอไม่ได้เลย
  if (!formData.supplier_id && isDeviceProcurementRequired(mode, original?.supplier_id)) {
    errors.supplier_id = 'กรุณาเลือกผู้จำหน่าย';
  }
  // ราคาซื้อ: ค่าติดลบผิดเสมอ ส่วน "ว่าง" ผิดเฉพาะเมื่อยังบังคับอยู่
  if (formData.purchase_price !== null && formData.purchase_price !== undefined && formData.purchase_price < 0) {
    errors.purchase_price = 'ราคาซื้อต้องเป็น 0 หรือมากกว่า';
  } else if (
    (formData.purchase_price === null || formData.purchase_price === undefined) &&
    isDeviceProcurementRequired(mode, original?.purchase_price)
  ) {
    errors.purchase_price = 'กรุณาระบุราคาซื้อที่ถูกต้อง';
  }
  return errors;
};

/**
 * ค่าฟอร์ม → payload ของ POST/PUT /devices (ผ่าน deviceService.convertFormToApiData อีกชั้น)
 * ส่งเฉพาะฟิลด์ที่ backend ต้องการ — ค่า undefined = ไม่ส่ง key → backend exclude_unset คงค่าเดิม (C8)
 */
export const buildDevicePayload = (
  formData: DeviceForm,
  mode: DeviceFormMode,
  selectedDevice?: Pick<Device, 'imei'> | null
) => ({
  serial_number: formData.is_serialized ? formData.serial_number : undefined,
  code: formData.code || undefined,
  model: formData.model || undefined,
  brand_id: formData.brand_id || undefined,
  device_type_id: formData.device_type_id || undefined,
  device_model_id: formData.device_model_id || undefined,
  // C8 + B-2: ส่ง imei "เฉพาะเมื่อผู้ใช้แก้จริง" เท่านั้น
  //
  // กติกา C8 ที่ต้องรักษาไว้: ห้ามส่ง `imei: formData.imei || ''` เด็ดขาด เพราะ
  //   1) เครื่องที่ imei = NULL จะถูกเขียนทับเป็น '' ตอนกดแก้ไขเรื่องอื่น (ข้อมูลเพี้ยนเงียบ)
  //   2) backend เช็คซ้ำแบบ `new_imei is not None` (devices.py:774-781) + คอลัมน์ unique
  //      → เครื่องที่ 2 ที่ imei เป็น NULL จะชน '' ของเครื่องแรก แล้ว 400 "IMEI นี้ถูกใช้งานแล้ว"
  //      ทั้งที่ผู้ใช้ไม่ได้ยุ่งกับ IMEI เลย
  // ตอนนี้ฟอร์มมีช่อง IMEI แล้ว (B-2) จึงเทียบกับค่าเดิมของเครื่อง:
  //   ไม่แตะ → undefined → convertFormToApiData ไม่ใส่ key → exclude_unset คงค่าเดิม
  //   ล้างช่อง → '' → service แปลงเป็น null (ไม่ใช่ '') → หลบ unique constraint ของ MySQL
  //                   ที่ยอมให้ NULL ซ้ำได้แต่ '' ซ้ำไม่ได้
  imei: (() => {
    const typed = (formData.imei || '').trim();
    if (mode === 'create') return typed || undefined;
    const original = (selectedDevice?.imei || '').trim();
    return typed === original ? undefined : typed;
  })(),
  status: formData.status as DeviceStatus,
  purchase_date: formData.purchase_date || undefined,
  notes: formData.notes || '',
  location: formData.location || undefined,
  location_note: formData.location_note || undefined,
  device_number: formData.device_number || undefined,
  is_serialized: formData.is_serialized,
  quantity: formData.is_serialized ? 1 : formData.quantity,
  unit: formData.unit || 'เครื่อง',
  minimum_quantity: formData.minimum_quantity || 0,
  // ใช้ `??` ไม่ใช่ `||` — ราคาซื้อ 0 บาทเป็นค่าที่ถูกต้อง (ของแถม/รับโอน)
  // `0 || undefined` = undefined ⇒ ตั้งราคาเป็น 0 ไม่ติดเลย ค่าเก่าค้างอยู่เงียบ ๆ
  // ค่าว่าง (null) → undefined → ไม่ส่ง key → คงค่าเดิมของเครื่องไว้ (C8)
  supplier_id: formData.supplier_id ?? undefined,
  purchase_price: formData.purchase_price ?? undefined,
  invoice_number: formData.invoice_number || undefined,
  receive_date: formData.receive_date || undefined,
  condition: formData.condition || undefined,
  book_value: formData.book_value || undefined,
  asset_number: formData.asset_number || undefined,
});

export const DEVICE_CONDITION_LABELS: Record<string, string> = {
  [DeviceCondition.NEW]: 'ใหม่',
  [DeviceCondition.USED]: 'มือสอง',
  [DeviceCondition.REFURBISHED]: 'ซ่อมแล้ว',
};

export const DEVICE_UNIT_OPTIONS = ['เครื่อง', 'ชิ้น', 'เส้น', 'อัน', 'ชุด', 'กล่อง'] as const;

/** ป้ายสถานะไทย — แหล่งเดียวกับ DeviceStatusBadge (config/designSystem statusColors.device) */
export const deviceStatusLabel = (status?: string | null): string => {
  if (!status) return '-';
  const cfg = (statusColors.device as Record<string, { label: string }>)[String(status).toUpperCase()];
  return cfg?.label ?? String(status);
};

/** ชื่อฟิลด์ภาษาไทย (การ์ด "ประวัติกิจกรรม") — คีย์ตรงกับ backend services/item_audit.DEVICE_SNAPSHOT_FIELDS */
export const DEVICE_FIELD_LABELS: Record<string, string> = {
  code: 'รหัสอุปกรณ์',
  device_type_id: 'ประเภทอุปกรณ์',
  brand_id: 'ยี่ห้อ',
  device_model_id: 'รุ่น',
  model: 'รุ่น (ข้อความ)',
  is_serialized: 'ประเภทการจัดการ',
  serial_number: 'Serial Number',
  imei: 'IMEI',
  quantity: 'จำนวนคงเหลือ',
  unit: 'หน่วยนับ',
  minimum_quantity: 'จำนวนขั้นต่ำ',
  status: 'สถานะ',
  purchase_date: 'วันที่ซื้อ',
  location: 'ตำแหน่งที่ตั้ง/สถานที่',
  location_note: 'หมายเหตุตำแหน่ง',
  device_number: 'ลำดับเครื่อง',
  supplier_id: 'ผู้จำหน่าย',
  purchase_price: 'ราคาซื้อ (บาท)',
  invoice_number: 'เลขที่ใบกำกับภาษี',
  receive_date: 'วันที่รับเข้าคลัง',
  condition: 'สภาพสินค้า',
  book_value: 'มูลค่าทางบัญชี (บาท)',
  asset_number: 'เลขครุภัณฑ์',
  notes: 'หมายเหตุ',
};

export const DEVICE_VALUE_FORMATTERS: Record<string, (v: unknown) => string> = {
  status: (v) => deviceStatusLabel(String(v)),
  is_serialized: (v) => (v ? 'ติดตาม Serial Number' : 'นับจำนวนคงคลัง'),
  condition: (v) => DEVICE_CONDITION_LABELS[String(v)] ?? String(v),
};
